import { useState } from "react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { api, money } from "@/lib/api";
import { sizeLabel } from "@/lib/sizes";
import { Minus, Plus, X, Trash2, ArrowRight, Truck, Scissors, Clock, MessageCircle, MapPin, CalendarDays } from "lucide-react";
import { trackBeginCheckout } from "@/lib/analytics";
import { SALE, saleActive } from "@/lib/sale";
import { toast } from "sonner";

const FREE_SHIP = 75;

export const CartDrawer = () => {
  const {
    items, open, setOpen, updateQty, removeItem, updateNote,
    rawSubtotal, count,
  } = useCart();
  const [loading, setLoading] = useState(false);
  const [fulfil, setFulfil] = useState("ship");
  const [pickupDate, setPickupDate] = useState("");
  const todayStr = new Date().toISOString().slice(0, 10);
  const maxPickupStr = new Date(Date.now() + 21 * 86400000).toISOString().slice(0, 10);

  const remaining = Math.max(0, FREE_SHIP - rawSubtotal);
  const shipPct = Math.min(100, (rawSubtotal / FREE_SHIP) * 100);

  const goToCheckout = async () => {
    const lines = items.filter((i) => i.variant_id).map((i) => ({ variant_id: i.variant_id, qty: i.qty }));
    if (!lines.length) { toast.error("Cart is empty."); return; }
    trackBeginCheckout(items, rawSubtotal);
    // Carry alteration details into the Shopify order note so the boutique
    // can match measurements (saved via /api/alterations) to the order.
    const altItems = items.filter((i) => i.isAlteration);
    const altNote = altItems.length
      ? "Alterations: " + altItems.map((i) =>
          `${i.title.replace("Alteration — ", "")} for "${i.forProduct}"${i.note ? ` [Measurements: ${i.note}]` : " [Measurements: not provided]"}`).join("; ")
      : null;
    const pickupNote = fulfil === "pickup"
      ? `STORE PICKUP on ${pickupDate ? new Date(`${pickupDate}T12:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" }) : "date TBD"} — 183 W Golf Rd, Schaumburg`
      : null;
    const note = [pickupNote, altNote].filter(Boolean).join(" | ") || null;
    setLoading(true);
    try {
      const { data } = await api.post("/checkout", { items: lines, discount: saleActive() ? SALE.code : null, note });
      window.location.href = data.url;
    } catch {
      toast.error("Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" data-testid="cart-drawer"
        className="bg-[#FFFFFF] w-full sm:max-w-md p-0 flex flex-col border-l border-[#E5E5DF] [&>button]:hidden">
        <SheetTitle className="sr-only">Your Shopping Bag</SheetTitle>
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#E5E5DF]">
          <h2 className="font-display text-2xl">Your Bag <span className="text-[#6E6E69] text-lg">({count})</span></h2>
          <button data-testid="cart-close" onClick={() => setOpen(false)} className="hover:text-[#B84A3B] transition-colors"><X size={20} /></button>
        </div>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 px-6">
            <p className="font-display text-2xl text-[#6E6E69]">Your bag is empty</p>
            <button onClick={() => setOpen(false)} className="overline border-b border-[#1A1A1A] pb-1 hover:text-[#B84A3B] transition-colors">Continue Shopping</button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
              <div className="bg-[#EFEFEA] px-4 py-3" data-testid="free-ship-bar">
                <div className="flex items-center gap-2 text-[0.7rem] font-mono tracking-wide text-[#1A1A1A]">
                  <Truck size={14} />
                  {remaining > 0 ? <span>You&apos;re <b>{money(remaining)}</b> away from FREE U.S. shipping</span>
                    : <span className="text-[#B84A3B]">You&apos;ve unlocked FREE U.S. shipping!</span>}
                </div>
                <div className="mt-2 h-1 bg-[#DDDDD5] overflow-hidden">
                  <div className="h-full bg-[#B84A3B] transition-all duration-500" style={{ width: `${shipPct}%` }} />
                </div>
              </div>
              {items.some((i) => i.isAlteration) && (
                <div className="flex items-start gap-2.5 bg-[#B8860B]/10 border border-[#B8860B]/30 px-4 py-3" data-testid="cart-alteration-note">
                  <Clock size={15} className="text-[#B8860B] mt-0.5 shrink-0" />
                  <p className="text-[0.72rem] font-body text-[#1A1A1A] leading-relaxed">
                    Your order includes an alteration — tailoring adds <b>2 days</b> to delivery. Altered pieces are final sale.
                  </p>
                </div>
              )}
              {items.map((i) => (
                <div key={i.key} data-testid={`cart-item-${i.slug}`} className="flex gap-4">
                  {i.image ? (
                    <img src={i.image} alt={i.title} className="w-20 h-28 object-cover bg-[#EFEFEA]" />
                  ) : (
                    <div className="w-20 h-28 bg-[#EFEFEA] flex items-center justify-center text-[#B84A3B]"><Scissors size={20} /></div>
                  )}
                  <div className="flex-1 flex flex-col">
                    <div className="flex justify-between gap-2">
                      <h3 className="font-body text-sm leading-snug line-clamp-2">{i.title}</h3>
                      <button data-testid={`cart-remove-${i.slug}`} onClick={() => removeItem(i.key)} className="text-[#6E6E69] hover:text-[#B84A3B] transition-colors shrink-0"><Trash2 size={15} /></button>
                    </div>
                    {i.isAlteration ? (
                      <span className="text-[0.68rem] font-mono text-[#6E6E69] mt-0.5">for {i.forProduct}</span>
                    ) : (i.color || i.size) ? (
                      <span className="text-[0.68rem] font-mono text-[#6E6E69] mt-0.5">
                        {[i.color, i.size && `Size ${sizeLabel(i.size)}`].filter(Boolean).join(" · ")}
                      </span>
                    ) : null}
                    {i.isAlteration && (
                      <textarea
                        data-testid={`alteration-note-${i.key}`}
                        value={i.note || ""}
                        onChange={(e) => updateNote(i.key, e.target.value)}
                        rows={2}
                        placeholder="Measurements in inches — e.g. Bust 36, Waist 30, shorten length 2"
                        className="mt-2 w-full text-xs font-body border border-[#E5E5DF] bg-[#FAF9F6] px-2.5 py-2 focus:outline-none focus:border-[#B84A3B] resize-none"
                      />
                    )}
                    <div className="mt-auto flex items-center justify-between">
                      {i.isAlteration ? (
                        <span className="text-[0.68rem] font-mono text-[#6E6E69]">tailored in-house</span>
                      ) : (
                        <div className="flex items-center border border-[#E5E5DF]">
                          <button data-testid={`cart-dec-${i.slug}`} onClick={() => updateQty(i.key, i.qty - 1)} className="w-7 h-7 flex items-center justify-center hover:bg-[#EFEFEA]"><Minus size={12} /></button>
                          <span className="w-7 text-center text-xs">{i.qty}</span>
                          <button data-testid={`cart-inc-${i.slug}`} onClick={() => updateQty(i.key, i.qty + 1)} className="w-7 h-7 flex items-center justify-center hover:bg-[#EFEFEA]"><Plus size={12} /></button>
                        </div>
                      )}
                      <span className="font-body text-sm">{money(i.price * i.qty)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-[#E5E5DF] px-6 py-5 space-y-3">
              <div className="flex justify-between font-display text-2xl">
                <span>Subtotal</span><span data-testid="cart-total">{money(rawSubtotal)}</span>
              </div>
              <div className="border border-[#E5E5DF] px-4 py-3 space-y-2.5" data-testid="cart-fulfilment">
                <p className="text-[0.62rem] font-mono tracking-[0.18em] uppercase text-[#6E6E69]">Delivery or pickup?</p>
                <div className="grid grid-cols-2 gap-2">
                  <button data-testid="cart-fulfil-ship" onClick={() => setFulfil("ship")}
                    className={`flex items-center justify-center gap-1.5 py-2.5 text-[0.62rem] font-mono tracking-wide uppercase border transition-colors ${fulfil === "ship" ? "bg-[#1A1A1A] text-white border-[#1A1A1A]" : "border-[#E5E5DF] text-[#6E6E69] hover:border-[#1A1A1A]"}`}>
                    <Truck size={13} /> Ship to me
                  </button>
                  <button data-testid="cart-fulfil-pickup" onClick={() => { setFulfil("pickup"); if (!pickupDate) setPickupDate(todayStr); }}
                    className={`flex items-center justify-center gap-1.5 py-2.5 text-[0.62rem] font-mono tracking-wide uppercase border transition-colors ${fulfil === "pickup" ? "bg-[#1A1A1A] text-white border-[#1A1A1A]" : "border-[#E5E5DF] text-[#6E6E69] hover:border-[#1A1A1A]"}`}>
                    <MapPin size={13} /> Boutique pickup
                  </button>
                </div>
                {fulfil === "pickup" && (
                  <div className="space-y-1.5">
                    <label htmlFor="cart-pickup-date" className="text-[0.62rem] font-mono text-[#6E6E69] flex items-center gap-1.5"><CalendarDays size={12} /> Pick a day — today or later</label>
                    <input id="cart-pickup-date" type="date" data-testid="cart-pickup-date" value={pickupDate}
                      min={todayStr} max={maxPickupStr}
                      onChange={(e) => setPickupDate(e.target.value)}
                      className="w-full border border-[#E5E5DF] px-3 py-2 text-sm font-body bg-white" />
                    <p className="text-[0.62rem] font-mono text-[#6E6E69] leading-relaxed">183 W Golf Rd, Schaumburg · Open daily 10:30 AM–8 PM · Choose "Pick up" at checkout to skip shipping fees</p>
                  </div>
                )}
              </div>
              <button data-testid="checkout-button" onClick={goToCheckout} disabled={loading}
                className="w-full bg-[#1A1A1A] text-[#FFFFFF] py-4 text-[0.72rem] font-mono tracking-[0.22em] uppercase hover:bg-[#B84A3B] transition-colors duration-300 flex items-center justify-center gap-2 disabled:opacity-60">
                {loading ? "Redirecting…" : <>Secure Checkout <ArrowRight size={14} /></>}
              </button>
              <button
                data-testid="cart-remind-me"
                onClick={() => { setOpen(false); window.dispatchEvent(new CustomEvent("kc:open-cart-reminder")); }}
                className="w-full text-center text-[0.62rem] font-mono tracking-wide text-[#6E6E69] hover:text-[#1FA855] transition-colors flex items-center justify-center gap-1.5"
              >
                <MessageCircle size={12} /> Remind me on WhatsApp
              </button>
              <p className="text-center text-[0.62rem] font-mono text-[#6E6E69] tracking-wide">Checkout securely via Shopify · Ships to USA &amp; Canada</p>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

